// src/components/ResetPassword.tsx
import React, { useState } from "react";
import {
  Box,
  Button,
  CssBaseline,
  FormControl,
  FormLabel,
  TextField,
  Typography,
  Card as MuiCard,
  Stack,
  Link,
} from "@mui/material";
import { styled } from "@mui/system";
import { resetPassword, confirmResetPassword } from "aws-amplify/auth";
import StripyBackground from "./DiagonalCanvas";
import { useTenantConfig } from "../../contexts/TenantConfigContext";

interface ResetPasswordProps {
  onResetSuccess: () => void;
  onResetFailure: (err: any) => void;
}

// Use the same styled Card as your SignIn page
const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: "auto",
  [theme.breakpoints.up("sm")]: {
    maxWidth: "450px",
  },
  boxShadow:
    "hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
}));

// Container styled similarly to your SignIn container
const ResetPasswordContainer = styled(Stack)(({ theme }) => ({
  height: "100vh",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(2),
  backgroundColor: "transparent",
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
}));

export const ResetPassword: React.FC<ResetPasswordProps> = ({
  onResetSuccess,
  onResetFailure,
}) => {
  const { config } = useTenantConfig();
  // "request", "confirm", and "done" steps
  const [step, setStep] = useState<"request" | "confirm" | "done">("request");
  const [username, setUsername] = useState("");
  const [confirmationCode, setConfirmationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // Step 1: Request a reset code
  const handleResetPasswordRequest = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();
    setError("");
    setMessage("");
    try {
      await resetPassword({ username });
      setMessage("A confirmation code has been sent to your email.");
      setStep("confirm");
    } catch (err: any) {
      setError(err.message || "Error requesting password reset.");
      onResetFailure(err);
    }
  };

  // Step 2: Confirm the reset with the code and new password
  const handleConfirmResetPassword = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();
    setError("");
    setMessage("");
    try {
      await confirmResetPassword({
        username,
        confirmationCode,
        newPassword,
      });
      setMessage(
        "Password reset successful! You can now sign in with your new password."
      );
      setStep("done");
    } catch (err: any) {
      setError(err.message || "Error confirming password reset.");
      onResetFailure(err);
    }
  };

  return (
    <>
      <StripyBackground />
      <CssBaseline />
      <Box
        sx={{
          position: "absolute",
          top: "1rem",
          left: "1rem",
          zIndex: 2,
          color: "#fff",
          fontWeight: "bold",
          fontSize: "1.5rem",
          display: "flex",
          alignItems: "center",
          gap: "20px",
        }}
      >
        <img src={config?.branding.logoUrl || "/brand/app-icon.svg"} alt={`${config?.branding.displayName || 'RAF'} Logo`} height="32" style={{ width: 'auto', borderRadius: '4px' }} />
        {config?.branding.displayName || 'RAF'}
      </Box>
      <ResetPasswordContainer>
        <Card>
          {step === "request" && (
            <Box
              component="form"
              onSubmit={handleResetPasswordRequest}
              sx={{ display: "flex", flexDirection: "column", gap: 4 }}
            >
              <Typography
                component="h1"
                variant="h4"
                sx={{ textAlign: "center" }}
              >
                Reset Password
              </Typography>
              {error && (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ textAlign: "center" }}
                >
                  {error}
                </Typography>
              )}
              {message && (
                <Typography
                  variant="body2"
                  color="green"
                  sx={{ textAlign: "center" }}
                >
                  {message}
                </Typography>
              )}
              <FormControl fullWidth>
                <FormLabel>Username (Email)</FormLabel>
                <TextField
                  id="username"
                  type="email"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="your@email.com"
                  required
                />
              </FormControl>
              <Button type="submit" variant="contained" fullWidth>
                Send Reset Code
              </Button>
              {/* Back button placed on its own row */}
              <Button
                variant="outlined"
                fullWidth
                onClick={onResetSuccess}
                sx={{
                  "&:hover": { backgroundColor: "transparent" },
                }}
              >
                Back
              </Button>
            </Box>
          )}
          {step === "confirm" && (
            <Box
              component="form"
              onSubmit={handleConfirmResetPassword}
              sx={{ display: "flex", flexDirection: "column", gap: 4 }}
            >
              <Typography
                component="h1"
                variant="h4"
                sx={{ textAlign: "center" }}
              >
                Confirm Reset Password
              </Typography>
              {error && (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ textAlign: "center" }}
                >
                  {error}
                </Typography>
              )}
              {message && (
                <Typography
                  variant="body2"
                  color="green"
                  sx={{ textAlign: "center" }}
                >
                  {message}
                </Typography>
              )}
              <FormControl fullWidth>
                <FormLabel>Confirmation Code</FormLabel>
                <TextField
                  id="confirmationCode"
                  type="text"
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value)}
                  placeholder="Enter code"
                  required
                />
              </FormControl>
              <FormControl fullWidth>
                <FormLabel>New Password</FormLabel>
                <TextField
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New Password"
                  required
                />
              </FormControl>
              <Button type="submit" variant="contained" fullWidth>
                Confirm Reset Password
              </Button>
              {/* Back button placed on its own row */}
              <Button
                variant="outlined"
                fullWidth
                onClick={onResetSuccess}
                sx={{
                  "&:hover": { backgroundColor: "transparent" },
                }}
              >
                Back
              </Button>
            </Box>
          )}
          {step === "done" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Typography
                component="h1"
                variant="h4"
                sx={{ textAlign: "center" }}
              >
                Password Reset Complete
              </Typography>
              {error && (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ textAlign: "center" }}
                >
                  {error}
                </Typography>
              )}
              {message && (
                <Typography
                  variant="body2"
                  color="green"
                  sx={{ textAlign: "center" }}
                >
                  {message}
                </Typography>
              )}
              <Button
                variant="contained"
                fullWidth
                onClick={onResetSuccess}
                sx={{
                  "&:hover": { backgroundColor: "transparent" },
                }}
              >
                Back to Sign In
              </Button>
            </Box>
          )}
        </Card>
      </ResetPasswordContainer>
    </>
  );
};
